import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import Button from '../../components/base/Button';

export default function AboutPage() {
  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      
      <main className="flex-grow">
        {/* Hero Section */}
        <section className="bg-gradient-to-r from-blue-900 to-blue-700 text-white py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              About Texas Workforce Academy
            </h1>
            <p className="text-xl md:text-2xl text-blue-100 max-w-3xl mx-auto">
              Building careers through affordable, high-quality, and efficient technical education
            </p>
          </div>
        </section>

        {/* Mission Section */}
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                  Our Mission
                </h2>
                <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                  Texas Workforce Academy is dedicated to providing accessible technical training that prepares students for successful careers in high-demand trades. We believe that quality education shouldn't come with a hefty price tag or years of commitment.
                </p>
                <p className="text-lg text-gray-700 leading-relaxed">
                  Our programs are designed to get you job-ready quickly, with practical skills that employers are actively seeking. Whether you're starting a new career or advancing in your current field, we're here to help you succeed.
                </p>
              </div>
              <div>
                <img 
                  src="https://public.readdy.ai/ai/img_res/8fd357282772e73d14ad6432647fff16.jpg" 
                  alt="Students learning technical skills" 
                  className="rounded-lg shadow-xl w-full h-auto object-cover object-top"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Three Pillars Section */}
        <section className="py-16 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-12">
              What Sets Us Apart
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {/* Affordable */}
              <div className="bg-white p-8 rounded-lg shadow-lg hover:shadow-xl transition-shadow">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-6">
                  <i className="ri-money-dollar-circle-line text-3xl text-blue-900"></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">
                  Affordable Education
                </h3>
                <p className="text-gray-700 leading-relaxed">
                  We offer competitive tuition rates and flexible payment options. Many students qualify for WIOA funding, which can cover the full cost of training at no expense to you.
                </p>
              </div>

              {/* High Quality */}
              <div className="bg-white p-8 rounded-lg shadow-lg hover:shadow-xl transition-shadow">
                <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mb-6">
                  <i className="ri-award-line text-3xl text-yellow-400"></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">
                  High-Quality Training
                </h3>
                <p className="text-gray-700 leading-relaxed">
                  As a Texas Workforce Commission-approved school, we maintain rigorous standards. Our experienced instructors bring real-world expertise and provide personalized attention in small class settings.
                </p>
              </div>

              {/* Efficient */}
              <div className="bg-white p-8 rounded-lg shadow-lg hover:shadow-xl transition-shadow">
                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-6">
                  <i className="ri-time-line text-3xl text-red-600"></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">
                  Efficient Programs
                </h3>
                <p className="text-gray-700 leading-relaxed">
                  Our short-term programs are designed to get you into the workforce quickly. Focus on what matters most—practical skills and experience that employers value.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Why Choose Us Section */}
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div className="order-2 lg:order-1">
                <img 
                  src="https://readdy.ai/api/search-image?query=mature%20adult%20student%20in%20his%2040s%20wearing%20safety%20equipment%20working%20with%20electrical%20tools%20in%20professional%20training%20workshop%20focused%20concentrated%20learning%20technical%20trade%20skills%20realistic%20modern%20facility&width=600&height=400&seq=about-why-1&orientation=landscape" 
                  alt="Student in training workshop" 
                  className="rounded-lg shadow-xl w-full h-auto object-cover object-top"
                />
              </div>
              <div className="order-1 lg:order-2">
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-8">
                  Why Choose Texas Workforce Academy?
                </h2>
                
                <div className="space-y-4">
                  <div className="flex items-start">
                    <i className="ri-check-line text-2xl text-green-600 mr-4 mt-1"></i>
                    <p className="text-lg text-gray-700">
                      <strong>Industry-Recognized Training:</strong> Learn skills that meet current industry standards and employer expectations
                    </p>
                  </div>
                  
                  <div className="flex items-start">
                    <i className="ri-check-line text-2xl text-green-600 mr-4 mt-1"></i>
                    <p className="text-lg text-gray-700">
                      <strong>Experienced Instructors:</strong> Learn from professionals who have worked in the trades and understand what it takes to succeed
                    </p>
                  </div>
                  
                  <div className="flex items-start">
                    <i className="ri-check-line text-2xl text-green-600 mr-4 mt-1"></i>
                    <p className="text-lg text-gray-700">
                      <strong>Job Placement Support:</strong> We connect trained workers with contractors and employers actively hiring
                    </p>
                  </div>
                  
                  <div className="flex items-start">
                    <i className="ri-check-line text-2xl text-green-600 mr-4 mt-1"></i>
                    <p className="text-lg text-gray-700">
                      <strong>Flexible Scheduling:</strong> Programs designed to work around your life and commitments
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Commitment Section */}
        <section className="py-16 bg-blue-50">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Our Commitment to You
            </h2>
            <p className="text-lg text-gray-700 mb-8 leading-relaxed">
              At Texas Workforce Academy, we're committed to your success. From the moment you enroll to the day you start your new career, we provide the support, resources, and training you need to achieve your goals. We measure our success by yours—and we're proud of the skilled professionals we've helped launch into rewarding careers.
            </p>
            <Button 
              href="/#contact" 
              variant="primary"
              icon={<i className="fas fa-arrow-right"></i>}
            >
              Start Your Journey Today
            </Button>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
